import { z } from 'zod';
/**
 * Límites del alcance cerrado (docs/01_MVP_SCOPE.md). Viven en el backend, no
 * solo en la interfaz: la UI puede saltárselos, la API no.
 */
export const LIMITS = {
    nameMinLength: 1,
    nameMaxLength: 120,
    descriptionMinLength: 1,
    descriptionMaxLength: 10_000,
    styleMaxLength: 2_000,
    musicPromptMaxLength: 4_000,
    totalDurationMinMs: 10_000,
    totalDurationMaxMs: 180_000,
    sceneDurationMinMs: 1_000,
    sceneDurationMaxMs: 30_000,
    sceneMinCount: 1,
    sceneMaxCount: 30,
    sceneTitleMaxLength: 200,
    sceneDescriptionMaxLength: 4_000,
    sceneTextMaxLength: 4_000,
    transitionMaxDurationMs: 1_000,
    fadeMaxMs: 1_000,
    gainMinDb: -60,
    gainMaxDb: 6,
};
export const FPS = 30;
/** Resolución exacta por formato. H.264/AAC, 30 fps. */
export const ASPECT_RATIO_SIZES = {
    '16:9': { width: 1920, height: 1080 },
    '9:16': { width: 1080, height: 1920 },
};
export const aspectRatioSchema = z.enum(['16:9', '9:16']);
/** Solo VIDEO_PODCAST opera en el MVP; los otros dos se muestran "Próximamente". */
export const projectTypeSchema = z.enum(['VIDEO_PODCAST', 'ADVANCED_VIDEO', 'VIRTUAL_CREATOR']);
export const AVAILABLE_PROJECT_TYPES = ['VIDEO_PODCAST'];
export const projectStatusSchema = z.enum([
    'DRAFT',
    'GENERATING',
    'REVIEW',
    'READY',
    'RENDERING',
    'COMPLETED',
    'ERROR',
]);
export const workflowRunStatusSchema = z.enum([
    'RUNNING',
    'WAITING_REVIEW',
    'COMPLETED',
    'FAILED',
    'CANCELLED',
]);
export const sceneMotionSchema = z.enum(['static', 'slow_zoom', 'pan_horizontal', 'pan_vertical']);
export const transitionTypeSchema = z.enum(['cut', 'crossfade']);
const gainDbSchema = z.number().min(LIMITS.gainMinDb).max(LIMITS.gainMaxDb);
const fadeMsSchema = z.number().int().min(0).max(LIMITS.fadeMaxMs);
/**
 * Configuración del proyecto. Se guarda como JSON pero se valida siempre con
 * este esquema: la columna Json no impone forma por sí sola.
 */
export const projectSettingsSchema = z.object({
    aspectRatio: aspectRatioSchema,
    targetDurationMs: z
        .number()
        .int()
        .min(LIMITS.totalDurationMinMs)
        .max(LIMITS.totalDurationMaxMs),
    style: z.string().max(LIMITS.styleMaxLength).default(''),
    /** Guion para generar la banda sonora; se usa cuando la música es generada. */
    musicPrompt: z.string().max(LIMITS.musicPromptMaxLength).default(''),
    musicEnabled: z.boolean().default(true),
    /** Referencia a la pista elegida. El servicio valida propiedad y asociación. */
    musicAssetId: z.uuid().nullable().default(null),
    musicGainDb: gainDbSchema.default(-18),
    musicLoop: z.boolean().default(true),
    /** La narración es opcional para el usuario (docs/00_PRODUCT.md). */
    voiceEnabled: z.boolean().default(false),
    voiceGainDb: gainDbSchema.default(0),
    /** Voz del proveedor de TTS (lista cerrada en ai-core). */
    voiceName: z.string().max(40).default('alloy'),
    /** Indicaciones de entrega para el TTS (tono, ritmo). No es texto a leer. */
    voiceStyle: z.string().max(300).default(''),
    voiceSpeed: z.number().min(0.5).max(1.5).default(1),
    fadeInMs: fadeMsSchema.default(500),
    fadeOutMs: fadeMsSchema.default(500),
});
/** Valores por defecto de un proyecto nuevo, derivados del propio esquema. */
export function defaultProjectSettings(overrides) {
    return projectSettingsSchema.parse(overrides);
}
export const createProjectRequestSchema = z.object({
    name: z.string().trim().min(LIMITS.nameMinLength).max(LIMITS.nameMaxLength),
    description: z
        .string()
        .trim()
        .min(LIMITS.descriptionMinLength)
        .max(LIMITS.descriptionMaxLength),
    type: projectTypeSchema,
    settings: projectSettingsSchema.partial().and(z.object({
        aspectRatio: aspectRatioSchema,
        targetDurationMs: z
            .number()
            .int()
            .min(LIMITS.totalDurationMinMs)
            .max(LIMITS.totalDurationMaxMs),
    })),
});
/**
 * Toda mutación de contenido declara la revisión que esperaba encontrar. Si no
 * coincide, la API responde 409 en vez de pisar el trabajo de otra pestaña.
 */
export const expectedRevisionSchema = z.number().int().min(1);
export const updateProjectRequestSchema = z
    .object({
    expectedRevision: expectedRevisionSchema,
    name: z.string().trim().min(LIMITS.nameMinLength).max(LIMITS.nameMaxLength).optional(),
    description: z
        .string()
        .trim()
        .min(LIMITS.descriptionMinLength)
        .max(LIMITS.descriptionMaxLength)
        .optional(),
    settings: projectSettingsSchema.partial().optional(),
})
    .refine((value) => value.name !== undefined || value.description !== undefined || value.settings !== undefined, { message: 'No hay nada que actualizar.' });
export const sceneSchema = z.object({
    id: z.uuid(),
    position: z.number().int().min(0).max(LIMITS.sceneMaxCount - 1),
    title: z.string(),
    description: z.string(),
    text: z.string().nullable(),
    durationMs: z.number().int().min(LIMITS.sceneDurationMinMs).max(LIMITS.sceneDurationMaxMs),
    visualAssetId: z.uuid().nullable(),
    voiceAssetId: z.uuid().nullable(),
    voiceOffsetMs: z.number().int().min(0),
    motion: sceneMotionSchema,
    transition: transitionTypeSchema,
    transitionDurationMs: z.number().int().min(0).max(LIMITS.transitionMaxDurationMs),
    revision: z.number().int().min(1),
});
export const updateSceneRequestSchema = z
    .object({
    expectedRevision: expectedRevisionSchema,
    title: z.string().trim().max(LIMITS.sceneTitleMaxLength).optional(),
    description: z.string().trim().max(LIMITS.sceneDescriptionMaxLength).optional(),
    text: z.string().max(LIMITS.sceneTextMaxLength).nullable().optional(),
    durationMs: z
        .number()
        .int()
        .min(LIMITS.sceneDurationMinMs)
        .max(LIMITS.sceneDurationMaxMs)
        .optional(),
    motion: sceneMotionSchema.optional(),
    transition: transitionTypeSchema.optional(),
    transitionDurationMs: z
        .number()
        .int()
        .min(0)
        .max(LIMITS.transitionMaxDurationMs)
        .optional(),
    voiceOffsetMs: z.number().int().min(0).optional(),
    /** `null` libera el hueco. El servicio valida propiedad, estado y tipo. */
    visualAssetId: z.uuid().nullable().optional(),
    voiceAssetId: z.uuid().nullable().optional(),
})
    .refine((value) => Object.keys(value).some((clave) => clave !== 'expectedRevision'), { message: 'No hay nada que actualizar.' });
/**
 * El reordenamiento llega completo: la lista debe ser exactamente las escenas
 * del proyecto, sin huecos ni repeticiones. Un reorden parcial dejaría
 * posiciones ambiguas.
 */
export const reorderScenesRequestSchema = z.object({
    expectedRevision: expectedRevisionSchema,
    sceneIds: z
        .array(z.uuid())
        .min(LIMITS.sceneMinCount)
        .max(LIMITS.sceneMaxCount)
        .refine((ids) => new Set(ids).size === ids.length, {
        message: 'La lista de escenas contiene identificadores repetidos.',
    }),
});
export const projectSummarySchema = z.object({
    id: z.uuid(),
    name: z.string(),
    description: z.string(),
    type: projectTypeSchema,
    status: projectStatusSchema,
    revision: z.number().int(),
    reviewedRevision: z.number().int().nullable(),
    archivedAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
    updatedAt: z.iso.datetime(),
    sceneCount: z.number().int().min(0),
});
export const projectDetailSchema = projectSummarySchema.extend({
    settings: projectSettingsSchema,
    scenes: z.array(sceneSchema),
});
/** Suma de las duraciones de escena. Es lo que durará el MP4, sin transiciones. */
export function totalSceneDurationMs(scenes) {
    return scenes.reduce((total, scene) => total + scene.durationMs, 0);
}
//# sourceMappingURL=project.js.map